/*
 * Post-render accessibility fixes for third-party widgets whose markup the theme cannot edit.
 *
 * Allyant's KB 943 sanctions this route for third-party components: "it may be possible to add
 * Javascript or other code after the component renders and fix some of the accessibility issues
 * (setting attributes for instance)". Every fix below is idempotent and only writes when the value
 * differs, so the observers never re-trigger themselves. If a vendor script never loads, nothing
 * here runs against it.
 *
 * Route package protection (cart page and cart drawer):
 *   KADA-106 / 231  "See Details" buttons stay in the tab order while the benefits panel is collapsed
 *   KADA-108 / 227  "Your order is protected." and "BENEFITS:" are not marked up as headings
 *   KADA-110 / 229  "Continue without package protection" is named "Checkout Without Coverage"
 *   KADA-233        The Route logo SVG is hidden from screen readers
 *   KADA-103        Hover-only "See what's included" tooltip; the visibility fix is CSS (base.css),
 *                   the Escape-to-dismiss handler is here
 *
 * Rebuy "You may also like" widget (cart page only, not the cart drawer):
 *   KEET-590        Widget title and product names skip a heading level after "Estimated total"
 *
 * Shopify Forms email popup (app embed, rendered in an open shadow root):
 *   KADA-203        Email field is not programmatically marked required
 *   KADA-204        Email field has autocomplete="off"
 *   KADA-208        Focus is not moved into the "Thank you for subscribing" dialog
 *   KADA-200        Shift+Tab can move focus out of the open dialog
 * Only stable hooks are used (role, aria-modal, aria-label, the label-email id) - never the app's
 * hashed CSS-module class names, which change on every app release.
 */
(() => {
  const setAttr = (el, name, value) => {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  };
  const removeAttr = (el, name) => {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  };

  // "Estimated total" is an <h2> in both the cart page footer and the cart drawer, so Route's
  // widget heading sits at level 3 and its "BENEFITS:" subheading at level 4.
  const ROUTE_HEADING_LEVEL = '3';
  const ROUTE_SUBHEADING_LEVEL = '4';

  function fixRoute(root) {
    // KADA-106 / 231: Route toggles aria-hidden on the panel but only clips it, so its buttons stay
    // focusable. Mirror its own state with inert. Two panels share this id on /cart (page + drawer).
    root.querySelectorAll('[id="protection-benefits"]').forEach((panel) => {
      const collapsed = panel.getAttribute('aria-hidden') === 'true';
      if (panel.inert !== collapsed) panel.inert = collapsed;
    });

    // KADA-108 / 227. The "BENEFITS:" span ships with an invalid role="benefits-title".
    root.querySelectorAll('.pw-route-protection .pw-coverage-description').forEach((el) => {
      setAttr(el, 'role', 'heading');
      setAttr(el, 'aria-level', ROUTE_HEADING_LEVEL);
    });
    root.querySelectorAll('.pw-route-protection .pw-cashback-benefits-title').forEach((el) => {
      setAttr(el, 'role', 'heading');
      setAttr(el, 'aria-level', ROUTE_SUBHEADING_LEVEL);
    });

    // KADA-110 / 229: Allyant's preferred fix is to drop the aria-label so the visible text is the name.
    root.querySelectorAll('.route-continue-without-coverage-link[aria-label]').forEach((el) => {
      removeAttr(el, 'aria-label');
    });

    // KADA-233: the logo is an icon SVG plus a wordmark SVG inside an aria-hidden span. Expose the
    // wordmark (the last SVG) as an image named "Route"; the icon beside it stays decorative.
    root.querySelectorAll('.pw-cashback-benefit-route-logo').forEach((logo) => {
      const wrapper = logo.querySelector(':scope > span[aria-hidden="true"]');
      if (wrapper) removeAttr(wrapper, 'aria-hidden');
      const wordmark = logo.querySelector('svg:last-of-type');
      if (!wordmark) return;
      removeAttr(wordmark, 'aria-hidden');
      setAttr(wordmark, 'role', 'img');
      setAttr(wordmark, 'aria-label', window.accessibilityStrings?.routeLogo || 'Route');
    });
  }

  // KADA-103: base.css now reveals the tooltip on keyboard focus as well as hover. Content shown on
  // focus must be dismissable without moving focus (SC 1.4.13), so Escape hides it until focus or
  // the pointer leaves the row.
  const TOOLTIP_ROW = '.pw-cashback-header-row-right';
  function onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (!(event.target instanceof Element)) return;
    const row = event.target.closest(TOOLTIP_ROW);
    if (row) row.setAttribute('data-tooltip-dismissed', '');
  }
  function clearDismissed(event) {
    if (!(event.target instanceof Element)) return;
    const row = event.target.closest(TOOLTIP_ROW);
    if (!row || row.contains(event.relatedTarget)) return;
    row.removeAttribute('data-tooltip-dismissed');
  }

  function watch(host) {
    fixRoute(host);
    // Route re-renders after every cart change, and the cart drawer swaps its section HTML.
    new MutationObserver(() => fixRoute(host)).observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'aria-label', 'role'],
    });
    host.addEventListener('keydown', onKeydown);
    host.addEventListener('focusout', clearDismissed);
    host.addEventListener('mouseout', clearDismissed);
  }

  // Both hosts persist across cart updates; only their inner HTML is replaced.
  document.querySelectorAll('cart-drawer, #main-cart-footer').forEach(watch);

  // ---------------------------------------------------------------------------------------------
  // Rebuy "You may also like" (cart page only)
  // ---------------------------------------------------------------------------------------------
  // KEET-590: on /cart the widget is its own section after the footer's "Estimated total" <h2>,
  // but its template hard-codes an <h4> title and <h5> product names. Its title is a sibling of
  // "Estimated total", not a child, so it gets level 2 and the products level 3. The same widget
  // also renders in the cart drawer, where it sits under an <h3> line-item title and those levels
  // are already correct, so only the cart page copy is re-levelled. aria-level instead of swapping
  // tags because Rebuy owns and re-renders these elements.
  const REBUY_TITLE = ':is(h1, h2, h3, h4, h5, h6).primary-title';
  const REBUY_PRODUCT_TITLE = ':is(h1, h2, h3, h4, h5, h6).rebuy-product-title';
  const REBUY_TITLE_LEVEL = '2';
  const REBUY_PRODUCT_LEVEL = '3';

  function fixRebuyCartPage(root) {
    root.querySelectorAll(`.rebuy-widget ${REBUY_TITLE}`).forEach((el) => {
      setAttr(el, 'aria-level', REBUY_TITLE_LEVEL);
    });
    root.querySelectorAll(`.rebuy-widget ${REBUY_PRODUCT_TITLE}`).forEach((el) => {
      setAttr(el, 'aria-level', REBUY_PRODUCT_LEVEL);
    });
  }

  if (document.body.classList.contains('template-cart')) {
    // The app block's [data-rebuy-id] placeholder is server-rendered; Rebuy fills it in later and
    // re-renders it after cart changes, so watch the section that holds it.
    const rebuySections = new Set();
    document.querySelectorAll('#MainContent [data-rebuy-id]').forEach((placeholder) => {
      const section = placeholder.closest('.shopify-section');
      if (section) rebuySections.add(section);
    });
    rebuySections.forEach((section) => {
      fixRebuyCartPage(section);
      new MutationObserver(() => fixRebuyCartPage(section)).observe(section, { childList: true, subtree: true });
    });
  }

  // ---------------------------------------------------------------------------------------------
  // Shopify Forms
  // ---------------------------------------------------------------------------------------------
  const FORMS_HOST = 'shopify-forms-embed';
  const FORMS_DIALOG = '[role="dialog"][aria-modal="true"][aria-label]';
  const FORMS_EMAIL = 'input[aria-labelledby="label-email"], input[type="email"]';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusedDialogs = new WeakSet();
  const watchedFormRoots = new WeakSet();

  const isVisible = (el) => el.getClientRects().length > 0;
  const focusablesIn = (dialog) => [...dialog.querySelectorAll(FOCUSABLE)].filter(isVisible);

  function fixForms(host, root) {
    // KADA-203 / 204. aria-required rather than required: native required would add the browser's
    // own validation bubble on top of the app's error message.
    root.querySelectorAll(FORMS_EMAIL).forEach((input) => {
      setAttr(input, 'aria-required', 'true');
      setAttr(input, 'autocomplete', 'email');
    });

    // KADA-208: after a successful submit the app swaps the form for a "Thank you" dialog with no
    // form fields, and focus drops to <body>. Move it to the dialog's first control (its close
    // button) once per dialog, and only if focus is not already somewhere the user chose.
    root.querySelectorAll(FORMS_DIALOG).forEach((dialog) => {
      if (focusedDialogs.has(dialog) || !isVisible(dialog) || dialog.querySelector('input')) return;
      const active = document.activeElement;
      if (active !== document.body && active !== host) return;
      const [first] = focusablesIn(dialog);
      if (!first) return;
      focusedDialogs.add(dialog);
      first.focus();
    });
  }

  // KADA-200: keep Tab and Shift+Tab inside the open dialog (Allyant KB 820's boundary wrap).
  function trapFormsFocus(event, root) {
    if (event.key !== 'Tab') return;
    const active = root.activeElement;
    const dialog = active?.closest(FORMS_DIALOG);
    if (!dialog) return;
    const items = focusablesIn(dialog);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    let target = null;
    if (event.shiftKey && active === first) target = last;
    else if (!event.shiftKey && active === last) target = first;
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    target.focus();
  }

  function watchForms(host) {
    const root = host.shadowRoot;
    if (!root || watchedFormRoots.has(root)) return;
    watchedFormRoots.add(root);
    fixForms(host, root);
    // The app renders and swaps its steps (form -> thank you) inside the shadow root.
    new MutationObserver(() => fixForms(host, root)).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['autocomplete', 'aria-required', 'style', 'class'],
    });
    root.addEventListener('keydown', (event) => trapFormsFocus(event, root), true);
  }

  // The app creates its host element at runtime and appends it to <body>.
  const watchNewForms = () => document.querySelectorAll(FORMS_HOST).forEach(watchForms);
  watchNewForms();
  customElements.whenDefined(FORMS_HOST).then(watchNewForms);
  new MutationObserver(watchNewForms).observe(document.body, { childList: true });
})();
